import { I18nResources } from 'app/src/model'
import { fetchI18nResources, updateI18nResources } from 'app/src/shared/helpers/i18nutils'
import { useHttpClient, useWebAppContext } from 'app/src/shared/hooks'
import libI18n, { Resource, use } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { Settings } from 'luxon'
import primeEn from 'primelocale/en.json'
import primeIt from 'primelocale/it.json'
import { addLocale, PrimeReactContext } from 'primereact/api'
import { PropsWithChildren, useContext, useEffect, useRef, useState } from 'react'
import { I18nextProvider, useTranslation } from 'react-i18next'

declare module 'i18next' {
  interface CustomTypeOptions {
    resources: I18nResources
    returnObjects: true
  }
}

export function LocaleManager(props: PropsWithChildren) {
  const [lastUpdate, setLastUpdate] = useState(Date.now())
  const { i18n } = useTranslation()
  const primeContext = useContext(PrimeReactContext)

  useEffect(() => {
    const language = i18n.language.split('-')[0]
    if (language !== Settings.defaultLocale) {
      primeContext.setLocale?.(language)
      Settings.defaultZone = 'system'
      Settings.defaultLocale = language
      setLastUpdate(Date.now())
    }
  }, [i18n.language, primeContext])

  return <div key={lastUpdate}>{props.children}</div>
}

export const I18nContextProvider = (props: PropsWithChildren) => {
  const httpClient = useHttpClient()
  const [ready, setReady] = useState<boolean>(false)
  const primeContext = useContext(PrimeReactContext)
  const context = useWebAppContext()
  const isInitializedRef = useRef<boolean>(false)

  useEffect(() => {
    const initializeI18n = async () => {
      const templateId = context.userInfo.welfareCampaign
        ? context.userInfo.welfareCampaign.configuration.frontend.templateId
        : 'DEFAULT'

      // TODO: valutare l'utilizzo del middleware i18next-http-backend (o rimuoverlo dal package json)
      const i18n = await fetchI18nResources(templateId, httpClient)

      if (i18n) {
        use(LanguageDetector).init(
          {
            ns: i18n?.namespaces,
            resources: i18n?.resources as Resource,
            debug: true,
            returnObjects: true,
            detection: {
              order: ['localStorage', 'navigator'],
            },
          },
          () => {
            addLocale('it', primeIt.it)
            addLocale('en', primeEn.en)
            const language = libI18n?.language?.split('-')[0] ?? 'it'
            primeContext.setLocale?.(language)
            Settings.defaultZone = 'system'
            Settings.defaultLocale = language
            setReady(true)
          },
        )
      }
    }
    if (httpClient && primeContext && !ready) {
      initializeI18n()
    }
  }, [httpClient, primeContext, ready, context])

  useEffect(() => {
    const templateId = context?.userInfo?.welfareCampaign?.configuration?.frontend?.templateId
    const frontend = context?.userInfo?.welfareCampaign?.configuration?.frontend

    if (templateId && frontend && ready && httpClient) {
      if (!isInitializedRef.current) {
        isInitializedRef.current = true
        return
      }

      console.log('[LocaleManager] Detected templateId change, updating i18n resources...')
      updateI18nResources(frontend, httpClient)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.userInfo?.welfareCampaign?.configuration?.frontend?.templateId, ready])

  return ready && <I18nextProvider i18n={libI18n!}>{props.children}</I18nextProvider>
}
